// File: src/pages/Admin/ProfileSubmission.js

import React, { useState, useEffect } from 'react';
import { auth, db, storage } from '../../firebase';
import {
  doc as docRef,
  setDoc,
  collection,
  query,
  where,
  getDocs,
  getDoc
} from 'firebase/firestore';
import {
  ref,
  uploadBytesResumable,
  getDownloadURL
} from 'firebase/storage';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';

export default function ProfileSubmission() {
  const [user, setUser] = useState(null);
  const [form, setForm] = useState({
    name: '',
    position: '',
    bio: '',
    linkedin: '',
    imageFile: null,
    imageUrl: '',
    education: ''
  });
  const [status, setStatus] = useState('');
  const navigate = useNavigate();

  // Guard: only logged-in admins may use this form
  useEffect(() => {
    return onAuthStateChanged(auth, async u => {
      if (!u) {
        navigate('/admin/login');
        return;
      }
      const snap = await getDoc(docRef(db, 'admins', u.email));
      if (!snap.exists()) {
        await signOut(auth);
        navigate('/admin/login');
        return;
      }
      setUser(u);
    });
  }, [navigate]);

  // If they've already submitted, load their existing record
  useEffect(() => {
    if (!user) return;
    const q = query(
      collection(db, 'profiles'),
      where('ownerUid', '==', user.uid)
    );
    getDocs(q).then(snap => {
      if (!snap.empty) {
        setForm({ ...snap.docs[0].data(), imageFile: null });
      }
    });
  }, [user]);

  const handleChange = e => {
    const { name, value, files } = e.target;
    if (name === 'imageFile') {
      setForm(f => ({ ...f, imageFile: files[0] }));
    } else {
      setForm(f => ({ ...f, [name]: value }));
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setStatus('Submitting…');

    // upload image if present
    let imageUrl = form.imageUrl;
    if (form.imageFile) {
      const storageRef = ref(
        storage,
        `profiles/${user.uid}/${form.imageFile.name}`
      );
      const uploadTask = await uploadBytesResumable(
        storageRef,
        form.imageFile
      );
      imageUrl = await getDownloadURL(uploadTask.ref);
    }

    // write to Firestore
    await setDoc(docRef(db, 'profiles', user.uid), {
      ownerUid:   user.uid,
      name:       form.name,
      position:   form.position,
      bio:        form.bio,
      linkedin:   form.linkedin,
      imageUrl,
      education:  form.education,
      status:     'pending',
      submittedAt: new Date()
    });

    setStatus('Submitted for review!');
  };

  if (!user) return <p>Loading…</p>;

  return (
    <form
      onSubmit={handleSubmit}
      style={{ maxWidth: 600, margin: 'auto', padding: '1rem' }}
    >
      <h2>Profile Submission</h2>

      <input
        name="name"
        value={form.name}
        onChange={handleChange}
        placeholder="Name"
        required
        style={{ width: '100%', marginBottom: 8 }}
      />

      <input
        name="position"
        value={form.position}
        onChange={handleChange}
        placeholder="Position"
        required
        style={{ width: '100%', marginBottom: 8 }}
      />

      <textarea
        name="bio"
        value={form.bio}
        onChange={handleChange}
        placeholder="Bio"
        rows={4}
        required
        style={{ width: '100%', marginBottom: 8 }}
      />

      <input
        name="linkedin"
        value={form.linkedin}
        onChange={handleChange}
        placeholder="LinkedIn URL"
        style={{ width: '100%', marginBottom: 8 }}
      />

      <input
        type="file"
        name="imageFile"
        accept="image/*"
        onChange={handleChange}
        style={{ marginBottom: 8 }}
      />

      <input
        name="education"
        value={form.education}
        onChange={handleChange}
        placeholder="Education"
        style={{ width: '100%', marginBottom: 12 }}
      />

      <button type="submit" style={{ width: '100%' }}>
        Submit for Review
      </button>

      {status && <p style={{ marginTop: 8 }}>{status}</p>}
    </form>
  );
}
